import React, { useState, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import api from '../api/axios';
import { useToast } from './Toast';
import MonthPicker from './MonthPicker';
import {
  CheckCircle, AlertTriangle,
  Plus, Edit2, X, Save, ChevronDown, Shield, Loader2, FileText,
  Clock, UserCheck, UserX, Timer
} from 'lucide-react';

// ── Statuts ───────────────────────────────────────────────────────────────────

const STATUS = {
  present:   { label: 'Présent',    accent: '#10B981', bg: 'rgba(209,250,229,0.8)',  dot: '#10B981', badge: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  en_attente:{ label: 'En attente', accent: '#F59E0B', bg: 'rgba(255,251,235,0.8)',  dot: '#F59E0B', badge: 'bg-amber-50 text-amber-600 border-amber-100' },
  parti:     { label: 'Parti',      accent: '#3B82F6', bg: 'rgba(239,246,255,0.8)',  dot: '#3B82F6', badge: 'bg-blue-50 text-blue-600 border-blue-100' },
  absent:    { label: 'Absent',     accent: '#CBD5E1', bg: 'rgba(241,245,249,0.5)',  dot: '#CBD5E1', badge: 'bg-slate-50 text-slate-400 border-slate-100' },
};

function getStatus(p) {
  if (!p) return STATUS.absent;
  if (p.depart) return STATUS.parti;
  if (p.arriveeStatut === 'en_attente') return STATUS.en_attente;
  return STATUS.present;
}

// ── Utilitaires ───────────────────────────────────────────────────────────────

function fmtTime(d) {
  if (!d) return '-';
  return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function fmtDate(str) {
  if (!str) return '-';
  return new Date(str + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
}

function fmtDuree(min) {
  if (min === null || min === undefined) return '-';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
}

function timeInputToISO(dateStr, timeStr) {
  if (!timeStr) return null;
  return `${dateStr}T${timeStr}:00`;
}

function initials(emp) {
  if (!emp) return '?';
  return `${emp.prenom?.[0] || ''}${emp.nom?.[0] || ''}`.toUpperCase();
}

// ── Sous-composants UI ────────────────────────────────────────────────────────

function Avatar({ emp, size = 36, accent }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: size / 2.8,
      background: accent + '22', border: `2px solid ${accent}44`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.38, fontWeight: 900, color: accent, flexShrink: 0,
    }}>
      {initials(emp)}
    </div>
  );
}

function StatusBadge({ p }) {
  const st = getStatus(p);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wide border ${st.badge}`}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: st.dot, display: 'inline-block', ...(st.dot === '#10B981' && !p?.depart ? { animation: 'pulse 2s infinite' } : {}) }} />
      {st.label}
    </span>
  );
}

function AdminBadge({ actions }) {
  if (!actions || actions.length === 0) return null;
  const last = actions[actions.length - 1];
  return (
    <span title={`${last.adminNom} - ${last.detail}`}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[9px] font-black uppercase tracking-wide">
      <Shield size={9} /> Admin
    </span>
  );
}

// ── Modal : Lire la note ──────────────────────────────────────────────────────
function NoteModal({ pointage, onClose }) {
  const empNom = pointage.employeeId ? `${pointage.employeeId.prenom} ${pointage.employeeId.nom}` : '';
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-t-[2rem] sm:rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="h-1 bg-slate-200 w-full" />
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-slate-100 flex items-center justify-center">
                <FileText size={16} className="text-slate-500" />
              </div>
              <div>
                <h3 className="font-black text-slate-800 text-sm">Note de fin de journée</h3>
                <p className="text-xs text-slate-400 mt-0.5">{empNom} · {fmtDate(pointage.date)}</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-slate-100 text-slate-400 transition-colors">
              <X size={15} />
            </button>
          </div>
          <div className="bg-slate-50 rounded-2xl p-4 max-h-[50vh] overflow-y-auto">
            <p className="text-slate-700 font-medium leading-relaxed whitespace-pre-wrap text-sm">{pointage.note}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Modal : Valider arrivée déclarée ─────────────────────────────────────────
function ValidateModal({ pointage, onClose, onSaved }) {
  const showToast = useToast();
  const [timeVal, setTimeVal] = useState(() => {
    if (!pointage.arriveeDeclaree) return '';
    const d = new Date(pointage.arriveeDeclaree);
    return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  });
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    setSaving(true);
    try {
      await api.put(`/pointage/${pointage._id}/valider`, { arriveeConfirmee: timeInputToISO(pointage.date, timeVal) });
      showToast('Arrivée validée !');
      onSaved();
      onClose();
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur', 'error');
    } finally { setSaving(false); }
  };

  const empNom = pointage.employeeId ? `${pointage.employeeId.prenom} ${pointage.employeeId.nom}` : '';

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-t-[2rem] sm:rounded-[2rem] shadow-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="h-1 bg-amber-400 w-full" />
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-amber-50 flex items-center justify-center">
                <AlertTriangle size={16} className="text-amber-500" />
              </div>
              <div>
                <h3 className="font-black text-slate-800 text-sm">Valider l'arrivée</h3>
                <p className="text-xs text-slate-400 mt-0.5">{empNom} · {fmtDate(pointage.date)}</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-slate-100 text-slate-400 transition-colors"><X size={15} /></button>
          </div>

          <div className="rounded-2xl border border-slate-100 bg-slate-50 divide-y divide-slate-100 mb-4">
            <div className="flex items-center justify-between px-4 py-2.5">
              <span className="text-xs text-slate-400 font-semibold">Heure déclarée</span>
              <span className="text-xs font-black text-slate-700">{fmtTime(pointage.arriveeDeclaree)}</span>
            </div>
            <div className="flex items-center justify-between px-4 py-2.5">
              <span className="text-xs text-slate-400 font-semibold">Heure du clic</span>
              <span className="text-xs font-bold text-slate-500">{fmtTime(pointage.arriveeSysteme)}</span>
            </div>
          </div>

          <div className="mb-4">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Confirmer l'heure</label>
            <input type="time" value={timeVal} onChange={e => setTimeVal(e.target.value)}
              className="w-full p-4 bg-slate-50 rounded-2xl outline-none font-black text-slate-700 text-lg focus:ring-2 ring-amber-400/30" />
          </div>

          <button onClick={handle} disabled={!timeVal || saving}
            className="w-full flex items-center justify-center gap-2 p-4 bg-amber-500 text-white rounded-2xl font-black text-sm disabled:opacity-40 hover:bg-amber-600 transition-colors">
            {saving ? <><Loader2 size={15} className="animate-spin" /> Validation…</> : <><CheckCircle size={15} /> Confirmer l'arrivée</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal : Éditer / Créer un pointage ───────────────────────────────────────
function EditModal({ pointage, employees, onClose, onSaved }) {
  const showToast = useToast();
  const isNew = !pointage;

  const [empId,   setEmpId]   = useState(pointage?.employeeId?._id || pointage?.employeeId || '');
  const [date,    setDate]    = useState(pointage?.date || new Date().toISOString().slice(0, 10));
  const [arrivee, setArrivee] = useState(() => {
    if (!pointage?.arrivee) return '';
    const d = new Date(pointage.arrivee);
    return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  });
  const [depart,  setDepart]  = useState(() => {
    if (!pointage?.depart) return '';
    const d = new Date(pointage.depart);
    return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  });
  const [note,   setNote]   = useState(pointage?.note || '');
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    setSaving(true);
    try {
      if (isNew) {
        await api.post('/pointage/admin', { employeeId: empId, date, arrivee: arrivee ? timeInputToISO(date, arrivee) : null, depart: depart ? timeInputToISO(date, depart) : null, note });
        showToast('Pointage créé !');
      } else {
        await api.put(`/pointage/${pointage._id}`, {
          arrivee: arrivee ? timeInputToISO(pointage.date, arrivee) : undefined,
          depart:  depart  ? timeInputToISO(pointage.date, depart)  : (depart === '' ? null : undefined),
          note,
        });
        showToast('Pointage mis à jour !');
      }
      onSaved();
      onClose();
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-t-[2rem] sm:rounded-[2rem] shadow-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="h-1 bg-blue-500 w-full" />
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center">
                {isNew ? <Plus size={16} className="text-blue-600" /> : <Edit2 size={16} className="text-blue-600" />}
              </div>
              <div>
                <h3 className="font-black text-slate-800 text-sm">{isNew ? 'Nouveau pointage' : 'Modifier le pointage'}</h3>
                {!isNew && pointage?.employeeId && (
                  <p className="text-xs text-slate-400 mt-0.5">{pointage.employeeId.prenom} {pointage.employeeId.nom} · {fmtDate(pointage.date)}</p>
                )}
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-slate-100 text-slate-400 transition-colors"><X size={15} /></button>
          </div>

          <div className="space-y-4">
            {isNew && (
              <>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Employé</label>
                  <div className="relative">
                    <select value={empId} onChange={e => setEmpId(e.target.value)}
                      className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none font-bold text-slate-700 text-sm appearance-none pr-10 focus:ring-2 ring-blue-400/20">
                      <option value="">Choisir un employé</option>
                      {employees.map(e => <option key={e._id} value={e._id}>{e.prenom} {e.nom}</option>)}
                    </select>
                    <ChevronDown size={13} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Date</label>
                  <input type="date" value={date} onChange={e => setDate(e.target.value)}
                    className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none font-bold text-slate-700 text-sm focus:ring-2 ring-blue-400/20" />
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Arrivée</label>
                <input type="time" value={arrivee} onChange={e => setArrivee(e.target.value)}
                  className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none font-bold text-slate-700 text-sm focus:ring-2 ring-blue-400/20" />
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Départ</label>
                <input type="time" value={depart} onChange={e => setDepart(e.target.value)}
                  className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none font-bold text-slate-700 text-sm focus:ring-2 ring-blue-400/20" />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Note (optionnel)</label>
              <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="Retard justifié, absence…"
                className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none font-medium text-slate-700 text-sm focus:ring-2 ring-blue-400/20" />
            </div>
          </div>

          {!isNew && pointage?.adminActions?.length > 0 && (
            <div className="mt-4 p-4 bg-violet-50 rounded-2xl">
              <p className="text-[9px] font-black text-violet-400 uppercase tracking-widest mb-2">Historique admin</p>
              <div className="space-y-1.5">
                {pointage.adminActions.map((a, i) => (
                  <div key={i} className="flex gap-2 text-xs">
                    <Shield size={10} className="text-violet-400 shrink-0 mt-0.5" />
                    <span className="text-slate-500">
                      <span className="font-black text-violet-700">{a.adminNom}</span> · {a.detail}
                      <span className="text-slate-300 ml-1">· {new Date(a.timestamp).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button onClick={handle} disabled={saving || (isNew && !empId)}
            className="mt-4 w-full flex items-center justify-center gap-2 p-4 bg-slate-900 text-white rounded-2xl font-black text-sm disabled:opacity-40 hover:bg-black transition-colors">
            {saving ? <><Loader2 size={15} className="animate-spin" /> Enregistrement…</> : <><Save size={15} /> Enregistrer</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────

const SOCKET_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '');

const AdminPointage = ({ employees = [], salonId }) => {
  const showToast = useToast();
  const [tab,       setTab]       = useState('today');
  const [todayData, setTodayData] = useState(null);
  const [history,   setHistory]   = useState([]);
  const [loading,   setLoading]   = useState(true);

  const [filterEmp,  setFilterEmp]  = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo,   setFilterTo]   = useState('');

  const [recapMonth,   setRecapMonth]   = useState(() => new Date().toISOString().slice(0, 7));
  const [recapData,    setRecapData]    = useState([]);
  const [recapLoading, setRecapLoading] = useState(false);

  const [validateP, setValidateP] = useState(null);
  const [editP,     setEditP]     = useState(null);
  const [showNew,   setShowNew]   = useState(false);
  const [noteP,     setNoteP]     = useState(null);

  const fetchToday = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/pointage/today');
      setTodayData(res.data.data);
    } catch { showToast('Erreur chargement pointages', 'error'); }
    finally { setLoading(false); }
  }, [showToast]);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterEmp)  params.set('employeeId', filterEmp);
      if (filterFrom) params.set('dateFrom', filterFrom);
      if (filterTo)   params.set('dateTo', filterTo);
      const res = await api.get(`/pointage?${params}`);
      setHistory(res.data.data);
    } catch { showToast('Erreur chargement historique', 'error'); }
    finally { setLoading(false); }
  }, [filterEmp, filterFrom, filterTo, showToast]);

  const fetchRecap = useCallback(async () => {
    setRecapLoading(true);
    try {
      const res = await api.get(`/pointage/monthly-summary?month=${recapMonth}`);
      setRecapData(res.data.data);
    } catch { showToast('Erreur chargement récapitulatif', 'error'); }
    finally { setRecapLoading(false); }
  }, [recapMonth, showToast]);

  useEffect(() => { if (tab === 'today')   fetchToday(); },   [tab, fetchToday]);
  useEffect(() => { if (tab === 'history') fetchHistory(); }, [tab, fetchHistory]);
  useEffect(() => { if (tab === 'recap')   fetchRecap(); },  [tab, fetchRecap]);

  useEffect(() => {
    if (!salonId) return;
    const socket = io(SOCKET_URL, { transports: ['websocket'] });
    socket.on('connect', () => socket.emit('join-salon', salonId));
    socket.on('pointage:update', () => fetchToday());
    return () => socket.disconnect();
  }, [salonId, fetchToday]);

  const pending = todayData?.pointages?.filter(p => p.arriveeStatut === 'en_attente') || [];

  const pointageByEmp = {};
  todayData?.pointages?.forEach(p => {
    pointageByEmp[p.employeeId?._id || p.employeeId] = p;
  });

  // Stats "aujourd'hui"
  const nbPresent    = todayData?.employees?.filter(e => { const p = pointageByEmp[e._id]; return p && !p.depart && p.arriveeStatut !== 'en_attente'; }).length || 0;
  const nbEnAttente  = pending.length;
  const nbParti      = todayData?.employees?.filter(e => pointageByEmp[e._id]?.depart).length || 0;
  const nbAbsent     = (todayData?.employees?.length || 0) - nbPresent - nbEnAttente - nbParti;

  const Spinner = () => (
    <div className="flex justify-center py-16">
      <div className="w-9 h-9 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-5 pb-6 animate-in fade-in duration-500">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-black text-slate-800">Pointage</h2>
          <p className="text-sm text-slate-400 font-medium mt-0.5">Suivi des présences et horaires</p>
        </div>
        <button onClick={() => setShowNew(true)}
          className="flex items-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-2xl font-black text-sm shadow-lg shadow-blue-100 hover:bg-blue-700 transition-colors">
          <Plus size={15} /> Ajouter un pointage
        </button>
      </div>

      {/* Alerte en attente */}
      {pending.length > 0 && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-100 rounded-2xl">
          <AlertTriangle size={16} className="text-amber-500 shrink-0" />
          <p className="text-sm font-bold text-amber-700">
            {pending.length} arrivée{pending.length > 1 ? 's' : ''} déclarée{pending.length > 1 ? 's' : ''} en attente de validation
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-2xl w-full sm:w-fit overflow-x-auto no-scrollbar">
        {[{ id: 'today', label: "Aujourd'hui" }, { id: 'history', label: 'Historique' }, { id: 'recap', label: 'Récapitulatif' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-5 py-2.5 rounded-xl font-black text-sm transition-all whitespace-nowrap shrink-0 ${
              tab === t.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── VUE AUJOURD'HUI ── */}
      {tab === 'today' && (
        <div className="space-y-4">
          {/* Stats strip */}
          {!loading && todayData && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { icon: UserCheck, label: 'Présents',   value: nbPresent,   color: '#10B981', bg: '#ECFDF5' },
                { icon: Timer,     label: 'En attente', value: nbEnAttente, color: '#F59E0B', bg: '#FFFBEB' },
                { icon: Clock,     label: 'Partis',     value: nbParti,     color: '#3B82F6', bg: '#EFF6FF' },
                { icon: UserX,     label: 'Absents',    value: nbAbsent,    color: '#94A3B8', bg: '#F8FAFC' },
              ].map(({ icon: Icon, label, value, color, bg }) => (
                <div key={label} className="rounded-2xl p-4 flex items-center gap-3" style={{ background: bg, border: `1px solid ${color}22` }}>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: color + '22' }}>
                    <Icon size={16} style={{ color }} />
                  </div>
                  <div>
                    <p className="text-2xl font-black leading-none" style={{ color }}>{value}</p>
                    <p className="text-[10px] font-bold text-slate-400 mt-0.5">{label}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
            {loading ? <Spinner /> : (
              <>
                {/* Mobile */}
                <div className="sm:hidden divide-y divide-slate-50">
                  {todayData?.employees?.map(emp => {
                    const p   = pointageByEmp[emp._id];
                    const st  = getStatus(p);
                    return (
                      <div key={emp._id} className="flex items-stretch">
                        <div className="w-1 shrink-0 rounded-r" style={{ background: st.accent, margin: '8px 0' }} />
                        <div className="flex-1 p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <Avatar emp={emp} size={36} accent={st.accent} />
                              <div className="min-w-0">
                                <p className="text-sm font-bold text-slate-800 truncate">{emp.prenom} {emp.nom}</p>
                                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                  <StatusBadge p={p} />
                                  {p?.adminActions?.length > 0 && <AdminBadge actions={p.adminActions} />}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0 ml-2">
                              {p?.arriveeStatut === 'en_attente' && (
                                <button onClick={() => setValidateP(p)} className="p-2 hover:bg-amber-50 text-amber-500 rounded-xl transition-colors">
                                  <CheckCircle size={15} />
                                </button>
                              )}
                              {p && (
                                <button onClick={() => setEditP(p)} className="p-2 hover:bg-blue-50 text-blue-500 rounded-xl transition-colors">
                                  <Edit2 size={15} />
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="mt-3 grid grid-cols-3 gap-2">
                            {[
                              { label: 'Arrivée', val: p ? fmtTime(p.arrivee) : '-' },
                              { label: 'Départ',  val: p?.depart ? fmtTime(p.depart) : (p ? '…' : '-') },
                              { label: 'Durée',   val: p ? fmtDuree(p.dureeMinutes) : '-' },
                            ].map(({ label, val }) => (
                              <div key={label} className="bg-slate-50 rounded-xl px-2 py-2 text-center">
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">{label}</p>
                                <p className="text-xs font-bold text-slate-700">{val}</p>
                              </div>
                            ))}
                          </div>
                          {p?.note && (
                            <div className="mt-2.5 px-3 py-2 bg-slate-50 rounded-xl">
                              <p className="text-xs text-slate-600 font-medium line-clamp-2">{p.note}</p>
                              {p.note.length > 60 && (
                                <button onClick={() => setNoteP(p)} className="text-[11px] font-black text-blue-500 hover:text-blue-700 mt-0.5 transition-colors">Voir tout</button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {(!todayData?.employees || todayData.employees.length === 0) && (
                    <p className="p-12 text-center text-slate-400 font-bold italic text-sm">Aucun employé</p>
                  )}
                </div>

                {/* Desktop */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50/60">
                        <th className="p-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Employé</th>
                        <th className="p-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Statut</th>
                        <th className="p-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Arrivée</th>
                        <th className="p-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Départ</th>
                        <th className="p-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Durée</th>
                        <th className="p-5 text-[10px] font-black uppercase tracking-widest text-slate-400">Note</th>
                        <th className="p-5 text-[10px] font-black uppercase tracking-widest text-slate-400 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {todayData?.employees?.map(emp => {
                        const p  = pointageByEmp[emp._id];
                        const st = getStatus(p);
                        return (
                          <tr key={emp._id} className="hover:bg-slate-50/40 transition-colors group">
                            <td className="p-0" style={{ borderLeft: `3px solid ${st.accent}` }}>
                              <div className="p-5 flex items-center gap-3">
                                <Avatar emp={emp} size={34} accent={st.accent} />
                                <div>
                                  <p className="text-sm font-bold text-slate-800">{emp.prenom} {emp.nom}</p>
                                  {p?.adminActions?.length > 0 && <AdminBadge actions={p.adminActions} />}
                                  {p?.isAdminCreated && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-600 text-[9px] font-black uppercase mt-0.5">
                                      <Shield size={9} /> Créé par admin
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="p-5"><StatusBadge p={p} /></td>
                            <td className="p-5">
                              <p className="text-sm font-bold text-slate-700">{p ? fmtTime(p.arrivee) : <span className="text-slate-300">-</span>}</p>
                            </td>
                            <td className="p-5">
                              <p className="text-sm font-bold text-slate-600">
                                {p?.depart ? fmtTime(p.depart) : (p ? <span className="text-slate-400">en cours…</span> : <span className="text-slate-300">-</span>)}
                              </p>
                            </td>
                            <td className="p-5">
                              <p className="text-sm font-bold text-slate-600">{p ? fmtDuree(p.dureeMinutes) : <span className="text-slate-300">-</span>}</p>
                            </td>
                            <td className="p-5 max-w-[200px]">
                              {p?.note ? (
                                <div>
                                  <p className="text-sm text-slate-600 font-medium line-clamp-2 leading-snug">{p.note}</p>
                                  {p.note.length > 60 && (
                                    <button onClick={() => setNoteP(p)} className="text-[11px] font-black text-blue-500 hover:text-blue-700 mt-1 transition-colors">Voir tout</button>
                                  )}
                                </div>
                              ) : <span className="text-slate-300 text-sm">-</span>}
                            </td>
                            <td className="p-5">
                              <div className="flex items-center justify-center gap-1">
                                {p?.arriveeStatut === 'en_attente' && (
                                  <button onClick={() => setValidateP(p)} title="Valider" className="p-2 hover:bg-amber-50 text-amber-500 rounded-xl transition-colors">
                                    <CheckCircle size={15} />
                                  </button>
                                )}
                                {p && (
                                  <button onClick={() => setEditP(p)} title="Modifier" className="p-2 hover:bg-blue-50 text-blue-500 rounded-xl transition-colors">
                                    <Edit2 size={15} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {(!todayData?.employees || todayData.employees.length === 0) && (
                        <tr><td colSpan={7} className="p-16 text-center text-slate-400 font-bold italic">Aucun employé</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── HISTORIQUE ── */}
      {tab === 'history' && (
        <div className="space-y-4">
          {/* Filtres */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-wrap items-center gap-3">
            <div className="relative">
              <select value={filterEmp} onChange={e => setFilterEmp(e.target.value)}
                className="appearance-none bg-slate-50 border border-slate-200 rounded-xl pl-4 pr-9 py-2.5 text-sm font-bold outline-none focus:ring-2 ring-blue-400/20">
                <option value="">Tous les employés</option>
                {employees.map(e => <option key={e._id} value={e._id}>{e.prenom} {e.nom}</option>)}
              </select>
              <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-400">Du</span>
              <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)}
                className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-bold outline-none focus:ring-2 ring-blue-400/20" />
              <span className="text-xs font-bold text-slate-400">au</span>
              <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)}
                className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-bold outline-none focus:ring-2 ring-blue-400/20" />
            </div>
            <button onClick={fetchHistory} className="px-5 py-2.5 bg-slate-900 text-white rounded-xl text-sm font-black hover:bg-black transition-colors">
              Filtrer
            </button>
          </div>

          <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
            {loading ? <Spinner /> : (
              <>
                {/* Mobile */}
                <div className="sm:hidden divide-y divide-slate-50">
                  {history.map(p => {
                    const st = getStatus(p);
                    return (
                      <div key={p._id} className="flex items-stretch">
                        <div className="w-1 shrink-0 rounded-r" style={{ background: st.accent, margin: '8px 0' }} />
                        <div className="flex-1 p-4">
                          <div className="flex items-center justify-between">
                            <div className="min-w-0">
                              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{fmtDate(p.date)}</p>
                              <p className="text-sm font-bold text-slate-800 truncate mt-0.5">
                                {p.employeeId ? `${p.employeeId.prenom} ${p.employeeId.nom}` : '-'}
                              </p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <StatusBadge p={p} />
                                {p.adminActions?.length > 0 && <AdminBadge actions={p.adminActions} />}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0 ml-2">
                              {p.arriveeStatut === 'en_attente' && (
                                <button onClick={() => setValidateP(p)} className="p-2 hover:bg-amber-50 text-amber-500 rounded-xl transition-colors">
                                  <CheckCircle size={15} />
                                </button>
                              )}
                              <button onClick={() => setEditP(p)} className="p-2 hover:bg-blue-50 text-blue-500 rounded-xl transition-colors">
                                <Edit2 size={15} />
                              </button>
                            </div>
                          </div>
                          <div className="mt-3 grid grid-cols-3 gap-2">
                            {[
                              { label: 'Arrivée', val: fmtTime(p.arrivee) },
                              { label: 'Départ',  val: fmtTime(p.depart) },
                              { label: 'Durée',   val: fmtDuree(p.dureeMinutes) },
                            ].map(({ label, val }) => (
                              <div key={label} className="bg-slate-50 rounded-xl px-2 py-2 text-center">
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">{label}</p>
                                <p className="text-xs font-bold text-slate-700">{val}</p>
                              </div>
                            ))}
                          </div>
                          {p.note && (
                            <div className="mt-2.5 px-3 py-2 bg-slate-50 rounded-xl">
                              <p className="text-xs text-slate-600 font-medium line-clamp-2">{p.note}</p>
                              {p.note.length > 60 && (
                                <button onClick={() => setNoteP(p)} className="text-[11px] font-black text-blue-500 hover:text-blue-700 mt-0.5 transition-colors">Voir tout</button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {history.length === 0 && <p className="p-12 text-center text-slate-400 font-bold italic text-sm">Aucun pointage</p>}
                </div>

                {/* Desktop */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50/60">
                        {['Date', 'Employé', 'Statut', 'Arrivée', 'Départ', 'Durée', 'Note', 'Actions'].map(h => (
                          <th key={h} className={`p-5 text-[10px] font-black uppercase tracking-widest text-slate-400 ${h === 'Actions' ? 'text-center' : ''}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {history.map(p => {
                        const st = getStatus(p);
                        return (
                          <tr key={p._id} className="hover:bg-slate-50/40 transition-colors">
                            <td className="p-0" style={{ borderLeft: `3px solid ${st.accent}` }}>
                              <div className="p-5">
                                <p className="text-sm font-bold text-slate-700">{fmtDate(p.date)}</p>
                              </div>
                            </td>
                            <td className="p-5">
                              <div className="flex items-center gap-2.5">
                                <Avatar emp={p.employeeId} size={30} accent={st.accent} />
                                <div>
                                  <p className="text-sm font-bold text-slate-800">{p.employeeId ? `${p.employeeId.prenom} ${p.employeeId.nom}` : '-'}</p>
                                  {p.adminActions?.length > 0 && <AdminBadge actions={p.adminActions} />}
                                </div>
                              </div>
                            </td>
                            <td className="p-5"><StatusBadge p={p} /></td>
                            <td className="p-5"><p className="text-sm font-bold text-slate-700">{fmtTime(p.arrivee)}</p></td>
                            <td className="p-5"><p className="text-sm font-bold text-slate-600">{fmtTime(p.depart)}</p></td>
                            <td className="p-5"><p className="text-sm font-bold text-slate-600">{fmtDuree(p.dureeMinutes)}</p></td>
                            <td className="p-5 max-w-[180px]">
                              {p.note ? (
                                <div>
                                  <p className="text-sm text-slate-600 font-medium line-clamp-2 leading-snug">{p.note}</p>
                                  {p.note.length > 60 && (
                                    <button onClick={() => setNoteP(p)} className="text-[11px] font-black text-blue-500 hover:text-blue-700 mt-1 transition-colors">Voir tout</button>
                                  )}
                                </div>
                              ) : <span className="text-slate-300 text-sm">-</span>}
                            </td>
                            <td className="p-5">
                              <div className="flex items-center justify-center gap-1">
                                {p.arriveeStatut === 'en_attente' && (
                                  <button onClick={() => setValidateP(p)} title="Valider" className="p-2 hover:bg-amber-50 text-amber-500 rounded-xl transition-colors">
                                    <CheckCircle size={15} />
                                  </button>
                                )}
                                <button onClick={() => setEditP(p)} title="Modifier" className="p-2 hover:bg-blue-50 text-blue-500 rounded-xl transition-colors">
                                  <Edit2 size={15} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {history.length === 0 && !loading && (
                        <tr><td colSpan={8} className="p-16 text-center text-slate-400 font-bold italic">Aucun pointage</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── RÉCAPITULATIF MENSUEL ── */}
      {tab === 'recap' && (
        <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-5 md:p-6 border-b border-slate-50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h3 className="font-black text-slate-800 text-base">Heures travaillées par employé</h3>
              <p className="text-xs text-slate-400 font-medium mt-0.5">Total des heures pointées sur le mois sélectionné</p>
            </div>
            <MonthPicker value={recapMonth} max={new Date().toISOString().slice(0, 7)} onChange={setRecapMonth} />
          </div>

          {recapLoading ? <Spinner /> : (() => {
            const maxMin = Math.max(...recapData.map(e => e.totalMinutes), 1);
            return (
              <>
                {/* Mobile */}
                <div className="sm:hidden divide-y divide-slate-50">
                  {recapData.map(emp => {
                    const total = fmtDuree(emp.totalMinutes);
                    const pct   = Math.round((emp.totalMinutes / maxMin) * 100);
                    return (
                      <div key={String(emp.employeeId)} className="px-5 py-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2.5">
                            <Avatar emp={emp} size={32} accent="#3B82F6" />
                            <div>
                              <p className="font-bold text-slate-800 text-sm">{emp.prenom} {emp.nom}</p>
                              <p className="text-xs text-slate-400 font-medium">{emp.jours} jour{emp.jours !== 1 ? 's' : ''}</p>
                            </div>
                          </div>
                          <p className={`text-sm font-black ${emp.totalMinutes > 0 ? 'text-blue-600' : 'text-slate-300'}`}>{total}</p>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-400 rounded-full transition-all" style={{ width: pct + '%' }} />
                        </div>
                      </div>
                    );
                  })}
                  {recapData.length === 0 && (
                    <p className="p-12 text-center text-slate-400 font-bold italic text-sm">Aucune donnée pour ce mois</p>
                  )}
                </div>

                {/* Desktop */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-50/60">
                        <th className="px-6 py-4">Collaborateur</th>
                        <th className="px-6 py-4 text-center">Jours</th>
                        <th className="px-6 py-4">Total heures</th>
                        <th className="px-6 py-4 text-center">Moy. / jour</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {recapData.map(emp => {
                        const total  = fmtDuree(emp.totalMinutes);
                        const avgMin = emp.jours > 0 ? Math.round(emp.totalMinutes / emp.jours) : 0;
                        const avg    = fmtDuree(avgMin);
                        const pct    = Math.round((emp.totalMinutes / maxMin) * 100);
                        return (
                          <tr key={String(emp.employeeId)} className="hover:bg-slate-50/40 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2.5">
                                <Avatar emp={emp} size={32} accent="#3B82F6" />
                                <p className="font-bold text-slate-800 text-sm">{emp.prenom} {emp.nom}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className="inline-flex items-center justify-center px-3 py-1 rounded-xl bg-slate-100 text-slate-600 font-black text-xs">{emp.jours}j</span>
                            </td>
                            <td className="px-6 py-4 w-56">
                              <div className="flex items-center gap-3">
                                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-blue-400 rounded-full" style={{ width: pct + '%' }} />
                                </div>
                                <span className={`text-sm font-black shrink-0 ${emp.totalMinutes > 0 ? 'text-blue-600' : 'text-slate-300'}`}>{total}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center text-slate-400 font-bold text-sm">{avg}</td>
                          </tr>
                        );
                      })}
                      {recapData.length === 0 && (
                        <tr><td colSpan={4} className="px-6 py-16 text-center text-slate-400 font-bold italic">Aucune donnée pour ce mois</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Légende */}
      <div className="flex flex-wrap gap-4 text-xs text-slate-400 font-medium">
        {Object.entries(STATUS).map(([, st]) => (
          <span key={st.label} className="flex items-center gap-1.5">
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: st.dot, display: 'inline-block' }} />
            {st.label}
          </span>
        ))}
        <span className="flex items-center gap-1.5">
          <Shield size={9} className="text-violet-400" /> Modifié par admin
        </span>
      </div>

      {/* Modals */}
      {validateP && (
        <ValidateModal pointage={validateP} onClose={() => setValidateP(null)}
          onSaved={() => { fetchToday(); if (tab === 'history') fetchHistory(); }} />
      )}
      {(editP || showNew) && (
        <EditModal pointage={editP || null} employees={employees}
          onClose={() => { setEditP(null); setShowNew(false); }}
          onSaved={() => { fetchToday(); if (tab === 'history') fetchHistory(); }} />
      )}
      {noteP && <NoteModal pointage={noteP} onClose={() => setNoteP(null)} />}
    </div>
  );
};

export default AdminPointage;
